import { useRef, useState, useEffect } from "react";
import { Camera, Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AvatarCropDialog } from "@/components/avatar-crop-dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const MAX_BYTES = 5 * 1024 * 1024;

export interface ProfileAvatarUploadProps {
  imageUrl?: string | null;
  initials: string;
  /** Return a Promise so the crop dialog can show uploading state until the request finishes. */
  onCroppedFile: (file: File) => void | Promise<void>;
  /** Optional: show spinner on avatar when upload is in progress (e.g. parent mutation.isPending) */
  isUploading?: boolean;
  avatarClassName?: string;
  fileInputTestId?: string;
}

export function ProfileAvatarUpload({
  imageUrl,
  initials,
  onCroppedFile,
  isUploading = false,
  avatarClassName = "w-20 h-20 sm:w-16 sm:h-16",
  fileInputTestId = "input-avatar-upload",
}: ProfileAvatarUploadProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropObjectUrlRef = useRef<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [pendingMime, setPendingMime] = useState("image/jpeg");
  const [cropBusy, setCropBusy] = useState(false);

  useEffect(() => {
    return () => {
      if (cropObjectUrlRef.current) {
        URL.revokeObjectURL(cropObjectUrlRef.current);
        cropObjectUrlRef.current = null;
      }
    };
  }, []);

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast({
        title: "File too large",
        description: "Please choose an image under 5MB",
        variant: "destructive",
      });
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file",
        description: "Please choose an image file",
        variant: "destructive",
      });
      return;
    }
    if (cropObjectUrlRef.current) {
      URL.revokeObjectURL(cropObjectUrlRef.current);
      cropObjectUrlRef.current = null;
    }
    setPendingMime(file.type || "image/jpeg");
    const url = URL.createObjectURL(file);
    cropObjectUrlRef.current = url;
    setCropSrc(url);
    setCropOpen(true);
  }

  function closeCrop() {
    setCropOpen(false);
    if (cropObjectUrlRef.current) {
      URL.revokeObjectURL(cropObjectUrlRef.current);
      cropObjectUrlRef.current = null;
    }
    setCropSrc(null);
  }

  async function handleCropConfirm(file: File) {
    setCropBusy(true);
    try {
      await Promise.resolve(onCroppedFile(file));
      closeCrop();
    } finally {
      setCropBusy(false);
    }
  }

  return (
    <>
      <div className="relative group cursor-pointer" onClick={openFilePicker}>
        <Avatar
          className={cn(avatarClassName, (isUploading || cropBusy) && "opacity-70 pointer-events-none")}
        >
          <AvatarImage src={imageUrl || undefined} />
          <AvatarFallback className="text-lg bg-primary/10 text-primary">{initials}</AvatarFallback>
        </Avatar>
        {(isUploading || cropBusy) && (
          <div className="absolute inset-0 rounded-full bg-background/50 flex items-center justify-center pointer-events-none">
            <Loader2 className="w-7 h-7 animate-spin text-primary" />
          </div>
        )}
        <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <Camera className="w-5 h-5 text-white" />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
          data-testid={fileInputTestId}
          disabled={isUploading || cropBusy}
        />
      </div>

      <AvatarCropDialog
        open={cropOpen}
        onOpenChange={(next) => {
          if (!next && !cropBusy) closeCrop();
        }}
        imageSrc={cropSrc}
        mimeType={pendingMime}
        onConfirm={(file) => void handleCropConfirm(file)}
        isApplying={cropBusy}
      />
    </>
  );
}
